import { Trash2, Edit, MapPin, Users, AlertCircle } from "lucide-react";
import { useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { type FilialInfo } from "@/types/structure";

interface FilialListSectionProps {
  company: any;
  filiais?: FilialInfo[];
  isLoading?: boolean;
  isError?: boolean;
  onRefresh?: () => void;
  onEdit?: (filial: FilialInfo) => void;
  onDelete?: (filialId: number) => void;
  onAddFilial?: () => void;
}

/**
 * Componente para listar filiais de uma Matriz
 * 
 * Estados suportados:
 * - Loading: Skeleton de 6 linhas com animação
 * - Error: Alert destrutivo com botão de retry
 * - Empty: Alert informativo com CTA para adicionar filial
 * - Data: Lista de filiais com ações (editar, deletar)
 */
export default function FilialListSection({
  company,
  filiais = [],
  isLoading = false,
  isError = false,
  onRefresh,
  onEdit,
  onDelete,
  onAddFilial,
}: FilialListSectionProps) {
  const [, navigate] = useLocation();
  // Skeleton de carregamento (enterprise)
  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Filiais</CardTitle>
          <CardDescription>Carregando filiais...</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div
              key={i}
              className="border rounded-lg p-4 space-y-3 animate-pulse"
            >
              <div className="flex justify-between items-start">
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-5 w-2/3" />
                  <Skeleton className="h-4 w-1/2" />
                </div>
                <Skeleton className="h-6 w-16" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-full" />
              </div>
              <div className="flex gap-2">
                <Skeleton className="h-9 w-20" />
                <Skeleton className="h-9 w-24" />
              </div>
            </div>
          ))}
        </CardContent>
      </Card>
    );
  }

  // Erro ao carregar (enterprise)
  if (isError) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Filiais</CardTitle>
        </CardHeader>
        <CardContent>
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              Erro ao carregar filiais. Tente novamente.
            </AlertDescription>
          </Alert>
          <Button
            variant="outline"
            size="sm"
            onClick={onRefresh}
            className="mt-4"
          >
            Tentar Novamente
          </Button>
        </CardContent>
      </Card>
    );
  }

  // Empty state (enterprise)
  if (filiais.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Filiais</CardTitle>
          <CardDescription>
            Nenhuma filial cadastrada para esta matriz
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              Você ainda não tem filiais cadastradas. Clique no botão abaixo para adicionar a primeira filial.
            </AlertDescription>
          </Alert>
          {onAddFilial && (
            <Button
              onClick={onAddFilial}
              className="mt-4 w-full"
            >
              + Adicionar Filial
            </Button>
          )}
        </CardContent>
      </Card>
    );
  }

  // Listar filiais (data state)
  return (
    <Card>
      <CardHeader>
        <CardTitle>Filiais</CardTitle>
        <CardDescription>
          {filiais.length} filial{filiais.length !== 1 ? "is" : ""} cadastrada{filiais.length !== 1 ? "s" : ""}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {filiais.map((filial) => (
            <div
              key={filial.id}
              className="border rounded-lg p-4 hover:bg-muted/50 transition-colors"
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <p className="text-sm font-medium text-foreground mb-1">
                    {filial.razaoSocial || filial.nomeFantasia || "(Sem razão social)"}
                  </p>
                  <h4 className="font-semibold text-base mb-1">
                    {filial.nomeFantasia}
                  </h4>
                  <p className="text-sm text-muted-foreground">
                    CNPJ: {filial.cnpj}
                  </p>
                </div>
                <Badge
                  variant={filial.status === "active" ? "default" : "secondary"}
                >
                  {filial.status === "active" ? "Ativa" : "Inativa"}
                </Badge>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <MapPin className="h-4 w-4" />
                  <span>
                    {filial.cidade}, {filial.estado}
                  </span>
                </div>
                {filial.employeesCount !== undefined && (
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Users className="h-4 w-4" />
                    <span>{filial.employeesCount} colaboradores</span>
                  </div>
                )}
              </div>

              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate(`/clients/${filial.id}?fromMatriz=${company?.id}`)}
                >
                  <Edit className="h-4 w-4 mr-2" />
                  Editar
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="text-destructive hover:text-destructive"
                  onClick={() => onDelete?.(filial.id)}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Remover
                </Button>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
